/**
 * Panel feel, analytic in presentation time (art §7.4 shake and zoom punch, §4.13 impact frames + the flash limiter).
 * Engine-local implementation of the `FeelApi` signature of `src/shared/cine/types.ts` until P2 lands `Feel` in
 * `src/shared/cine` (the renderer can swap to it without API changes). Pure: no DOM, no Date.
 */
import type { FeelApi, FeelOut } from '../shared';
import { IMPACT, PUNCH, SHAKE } from '../content/fx/timescales';

/** Smooth 1D value noise in [-1, 1] (deterministic). */
function noise1(x: number, seed: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const h = (n: number): number => {
    let v = Math.imul(n ^ seed, 0x27d4eb2d);
    v ^= v >>> 15; v = Math.imul(v, 0x85ebca6b); v ^= v >>> 13;
    return ((v >>> 0) / 4294967296) * 2 - 1;
  };
  const u = f * f * (3 - 2 * f);
  return h(i) * (1 - u) + h(i + 1) * u;
}

export class EngineFeel implements FeelApi {
  private traumaAt = -1e9;
  private traumaLevel = 0;
  private ampPx = 0;
  private punchAt = -1e9;
  private punchPct = 0;
  private punchIn: number = PUNCH.inMs;
  private punchHold = 0;
  private punchOut: number = PUNCH.outMs;
  /** Accepted impact sequences (start ms) in the last few seconds. */
  private readonly impacts: number[] = [];
  private impactKind: 'crit' | 'ko' = 'crit';
  private pulseAt = -1e9;
  private pulseCrit = false;
  /** Impact frames actually shown (art AT-11 `impactFramesShown`). */
  impactFramesShown = 0;
  readonly out: FeelOut = { shakePx: [0, 0], shakeRotDeg: 0, fovScale: 1, impactFrame: 0, vignettePulse: 0, flashFramesLast1s: 0 };
  /** 1 when the last pulse was a crit/KO (vermilion tint). */
  pulseVermilion = 0;

  constructor(private opts: { reducedMotion: boolean; flashes: boolean; panelScale: () => number }) {}

  setOptions(o: { reducedMotion: boolean; flashes: boolean }): void { this.opts = { ...this.opts, ...o }; }

  private traumaNow(t: number): number {
    if(t<this.traumaAt)return 0;
    return Math.max(0, this.traumaLevel - (SHAKE.decayPerS * (t - this.traumaAt)) / 1000);
  }

  kick(ampPx: number, atMs: number): void {
    if (this.opts.reducedMotion || ampPx <= 0) return;
    const cur = this.traumaNow(atMs);
    this.traumaLevel = Math.min(1, cur + Math.min(1, ampPx / 10));
    this.traumaAt = atMs;
    this.ampPx = Math.min(SHAKE.maxPx, Math.max(this.ampPx * (cur > 0 ? 1 : 0), ampPx));
  }

  punch(pct: number, atMs: number, inMs: number = PUNCH.inMs, holdMs = 0, outMs: number = PUNCH.outMs): void {
    if (this.opts.reducedMotion) return;
    this.punchAt = atMs; this.punchPct = pct; this.punchIn = inMs; this.punchHold = holdMs; this.punchOut = outMs;
  }

  /** Flash limiter: a sequence (negative 33 + white 17 = 50 ms) is accepted only if no other sequence started in the
   *  previous 1000 ms (≤ 50 ms of flash in any 1 s window, which also keeps ≥ 600 ms between sequences). */
  impact(kind: 'crit' | 'ko', atMs: number): boolean {
    const last = this.impacts.length ? this.impacts[this.impacts.length - 1] : -1e9;
    const allowed = this.opts.flashes && !this.opts.reducedMotion && atMs - last >= 1000 && atMs - last >= IMPACT.minGapMs;
    if (!allowed) {
      this.pulseAt = atMs; this.pulseCrit = true;
      return false;
    }
    this.impacts.push(atMs);
    if (this.impacts.length > 8) this.impacts.shift();
    this.impactKind = kind;
    return true;
  }

  get lastImpactKind(): 'crit' | 'ko' { return this.impactKind; }

  evaluate(presentMs: number): Readonly<FeelOut> {
    const o = this.out;
    const tr = this.traumaNow(presentMs);
    const k = this.opts.panelScale();
    if (tr > 0) {
      const a = tr * tr * this.ampPx * k;
      const ts = (presentMs / 1000) * SHAKE.noiseHz;
      o.shakePx[0] = a * noise1(ts, 17);
      o.shakePx[1] = a * noise1(ts, 91);
      o.shakeRotDeg = SHAKE.rotDeg * tr * tr * noise1(ts, 233);
    } else {
      o.shakePx[0] = 0; o.shakePx[1] = 0; o.shakeRotDeg = 0;
    }
    // zoom punch: in (ease-out cubic), hold, out (ease-in-out sine)
    const pt = presentMs - this.punchAt;
    let p = 0;
    if (pt >= 0) {
      if (pt < this.punchIn) { const x = pt / this.punchIn; p = 1 - (1 - x) ** 3; }
      else if (pt < this.punchIn + this.punchHold) p = 1;
      else if (pt < this.punchIn + this.punchHold + this.punchOut) { const x = (pt - this.punchIn - this.punchHold) / this.punchOut; p = 0.5 + 0.5 * Math.cos(Math.PI * x); }
    }
    o.fovScale = 1 - (this.punchPct / 100) * p;
    // impact frames
    o.impactFrame = 0;
    let flashMs = 0;
    for (const s of this.impacts) {
      const dt = presentMs - s;
      if (dt >= 0 && dt < IMPACT.negativeMs) o.impactFrame = 1;
      else if (dt >= IMPACT.negativeMs && dt < IMPACT.negativeMs + IMPACT.whiteMs) o.impactFrame = 2;
      if (dt >= 0 && dt < 1000) flashMs += IMPACT.negativeMs + IMPACT.whiteMs;
    }
    o.flashFramesLast1s = Math.round(flashMs / 16.7);
    const pd = presentMs - this.pulseAt;
    o.vignettePulse = pd >= 0 && pd < IMPACT.pulseMs ? IMPACT.pulseStrength * (1 - pd / IMPACT.pulseMs) : 0;
    this.pulseVermilion = this.pulseCrit ? 1 : 0;
    return o;
  }

  /** Flash ms inside the 1 s window ending at presentMs (art AT-11 `flashMsLast1s`). */
  flashMsLast1s(presentMs: number): number {
    let ms = 0;
    for (const s of this.impacts) {
      const dt = presentMs - s;
      if (dt >= 0 && dt < 1000) ms += Math.min(IMPACT.negativeMs + IMPACT.whiteMs, dt + 1);
    }
    return ms;
  }

  isActive(presentMs: number): boolean {
    const o = this.evaluate(presentMs);
    return o.shakePx[0] !== 0 || o.shakePx[1] !== 0 || o.fovScale !== 1 || o.impactFrame !== 0 || o.vignettePulse > 0;
  }
}
